import { canonicalizeKey } from "./keys";
import { GLOBAL_ENTITY, setUiConfigPermissions } from "./uiConfigEngine";
import type { PermissionMap } from "./types";

export interface Visibility {
  value?: unknown;
  isVisible: boolean;
  enable?: {
    isEnable: boolean;
  };
}

export interface DashboardInfoButton {
  visible: Visibility;
  label?: string;
  component?: { componentType: string };
  action?: { type: string };
}

export interface DashboardInfoTab {
  id: string;
  type: string;
  name: string;
  visible: Visibility;
  rendererType: string;
  resourceName: string;
  casbinResource: string;
  position: number;
  route: string;
  buttons?: Record<string, DashboardInfoButton>;
  /** Nested SUBTAB entries, e.g. "verifications" -> "verification_vkyc". */
  children?: DashboardInfoTab[];
}

export interface DashboardInfoSidebar {
  buttons?: Record<string, DashboardInfoButton>;
}

export interface DashboardInfoResponse {
  ruleData?: {
    uiElements?: {
      uiDashboardInfo?: {
        dashboardID?: string;
        uniqueID?: string;
        dashboardType?: string;
        tabs?: DashboardInfoTab[] | null;
        sidebar?: DashboardInfoSidebar | null;
        buttons?: Record<string, DashboardInfoButton> | null;
      };
    };
  };
}

function visibleButtonKeys(
  buttons: Record<string, DashboardInfoButton> | null | undefined
): string[] {
  return Object.entries(buttons ?? {})
    .filter(([, button]) => button?.visible?.isVisible)
    .map(([key]) => canonicalizeKey(key));
}

export function applyDashboardInfoUiConfig(
  response: DashboardInfoResponse | null | undefined,
  pageCasbinResource?: string
): void {
  const dashboardInfo = response?.ruleData?.uiElements?.uiDashboardInfo;
  const tabs = dashboardInfo?.tabs ?? [];
  const sidebarActions = visibleButtonKeys(dashboardInfo?.sidebar?.buttons);

  const permissionMap: PermissionMap = {};

  const registerTab = (tab: DashboardInfoTab, parentVisible: boolean): void => {
    const isVisible = parentVisible && !!tab.visible?.isVisible;
    const entities: Record<string, string[]> = {};
    if (isVisible) {
      entities[GLOBAL_ENTITY] = [
        ...visibleButtonKeys(tab.buttons),
        ...sidebarActions
      ];
    }

    if (tab.casbinResource) {
      permissionMap[canonicalizeKey(tab.casbinResource)] = entities;
    }

    for (const child of tab.children ?? []) {
      registerTab(child, isVisible);
    }
  };

  for (const tab of tabs) {
    registerTab(tab, true);
  }

  if (
    pageCasbinResource &&
    (dashboardInfo?.buttons || dashboardInfo?.sidebar)
  ) {
    permissionMap[canonicalizeKey(pageCasbinResource)] = {
      [GLOBAL_ENTITY]: [
        ...visibleButtonKeys(dashboardInfo?.buttons),
        ...sidebarActions
      ]
    };
  }

  setUiConfigPermissions(permissionMap);
}
